import React, {useEffect} from 'react';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import {BaseModal} from 'components/modals';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios, {AxiosResponse} from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {buildURL} from '@/utils/urlBuilder';
import CellLoader from '@/components/loading/CellLoader';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

interface UserApiLogin {
    createdOn: string;
    endpoint: string;
    method: string;
    ip: string;
    browserAgent: string;
}

interface ApiLoginsResponse {
    userApiLogins: UserApiLogin[];
    pagesList: number[];
}

interface UserApiLoginsModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    activeUserId: number;
}

const userApiLoginsUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<ApiLoginsResponse>> =>
    axios.get(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const userApiLoginsUpdateRequestDebounced = AwesomeDebouncePromise(userApiLoginsUpdateRequest, 350);

const UserApiLoginsModal: React.FC<UserApiLoginsModalProps> = React.memo((
    {
        modalOpen,
        closeModal,
        activeUserId,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const userLogins = useStoreState((state: any) => state.usersApiLogins.items);
    const storeUserLogins = useStoreActions((actions: any) => actions.usersApiLogins.loadUserLogins);

    const searchPagesList = useStoreState((state: any) => state.usersApiLogins.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions: any) => actions.usersApiLogins.setSearchPagesList);

    const searchCurrentPage = useStoreState((state: any) => state.usersApiLogins.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions: any) => actions.usersApiLogins.setSearchCurrentPage);

    const loadingUserLogins = useStoreState((state: any) => state.usersApiLogins.loading);
    const setLoadingUserLogins = useStoreActions((actions: any) => actions.usersApiLogins.setLoading);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setLoadingUserLogins(true);

            const paramsDict = {
                page: searchCurrentPage,
            };

            const response = await userApiLoginsUpdateRequestDebounced(
                buildURL('usersApiLogins', {obj_id: activeUserId}), paramsDict,
            );

            storeUserLogins(response.data.userApiLogins);
            storeSearchPagesList(response.data.pagesList);

            setLoadingUserLogins(false);
        };

        if (activeUserId !== 0) {
            fetchData();
        }
    }, [
        searchCurrentPage,
        storeSearchPagesList,
        storeUserLogins,
        activeUserId,
    ]);

    const pageChanged = (e: React.MouseEvent<HTMLElement>): void => {
        const target = e.target as HTMLElement;
        if (target.textContent) {
            storeSearchCurrentPage(parseInt(target.textContent, 10));
        }
    };

    const handlePageChange = (page: number) => {
        storeSearchCurrentPage(page);
    };

    const columns = [
        {
            id: 'i18ntthdatecreated3text',
            header: i18n.t('th_date_created.text'),
            accessorFn: (cell: UserApiLogin) => {
                if (!cell.createdOn || cell.createdOn === '-') {
                    return '-';
                }

                return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_endpoint.text'),
            accessorKey: 'endpoint',
        },
        {
            header: i18n.t('th_method.text'),
            accessorKey: 'method',
        },
        {
            header: i18n.t('th_text_ip.text'),
            accessorKey: 'ip',
        },
        {
            header: i18n.t('th_browser_agent.text'),
            accessorKey: 'browserAgent',
        },
    ];

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            title={i18n.t('jt_user_api.text')}
            size="xl"
        >
            {
                loadingUserLogins ? (
                    <CellLoader />
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                <TableComponent data={userLogins} columns={columns} />
                                <PaginationComponent
                                    pages={searchPagesList}
                                    currentPage={searchCurrentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </>
                )
            }
        </BaseModal>
    );
});

UserApiLoginsModal.displayName = 'UserApiLoginsModal';

export default UserApiLoginsModal; 